import React, { ElementType, ReactNode } from 'react';
import classNames from 'classnames';
import { FormGroupContextProvider } from './FormGroupContext';
import { FORM_CONTROL_SIZES } from './constants';

interface Props {
  /** Specifies contents of the component. */
  children: ReactNode;
  /** Specifies class name to append to the base element. */
  className?: string;
  /** Specifies base element for the component. */
  as?: ElementType;
  /** Specifies id to use in the group, it will be used as `htmlFor` in `FormLabel` and as `id` in input components.
   *  Will be autogenerated if none is supplied. */
  controlId?: string;
  /** Specifies whether to display components in invalid state, this affects styling. */
  isInvalid?: boolean;
  /** Specifies whether to display components in valid state, this affects styling. */
  isValid?: boolean;
  /** Specifies size for the component. Either `'sm'` or `'lg'`. */
  size?: typeof FORM_CONTROL_SIZES.SMALL | typeof FORM_CONTROL_SIZES.LARGE;
}

function FormGroup<As extends React.ElementType = 'div'>({
  children,
  controlId,
  isInvalid = false,
  isValid = false,
  size,
  as,
  ...props
}: { as?: As } & Props & React.ComponentPropsWithoutRef<As>) {
  return React.createElement(
    as ?? 'div',
    {
      ...props,
      className: classNames('pgn__form-group', props.className),
    }, (
      <FormGroupContextProvider
        controlId={controlId}
        isInvalid={isInvalid}
        isValid={isValid}
        size={size}
      >
        {children}
      </FormGroupContextProvider>
    ),
  );
}

export default FormGroup;
